import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { contractPdf } from "@/lib/contractPdf";
import { SignContractDialog } from "@/components/SignContractDialog";
import { SiaeInfo } from "@/components/SiaeCard";
import { Download, PenLine, XCircle, Clock, MessageSquare } from "lucide-react";

export const STATUS = {
  pending_artist: ["In attesa di controfirma", "chip-gold"],
  confirmed: ["Confermato", "chip-navy"],
  rejected: ["Rifiutato", "chip-navy"],
  expired: ["Scaduto", "chip-navy"],
  broker_request: ["Richiesta al Portale", "chip-navy"],
};

const hoursLeft = (iso) => {
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return "scaduto";
  const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m rimaste`;
};

export const BookingCard = ({ b, isProvider, isNew, onChanged }) => {
  const [signOpen, setSignOpen] = useState(false);
  const [label, chip] = STATUS[b.status] || [b.status, "chip-navy"];
  const canAnswer = isProvider && b.status === "pending_artist";
  const party = isProvider ? b.clientName : b.artistName;
  const nav = useNavigate();
  const otherId = isProvider ? b.clientId : b.artistId;
  const openChat = () => nav(`/chat?to=${otherId}&msg=${encodeURIComponent(`Riguardo al contratto per "${b.eventType}" del ${b.eventDate} a ${b.eventLocation}: `)}`);

  const countersign = async ({ signature, signerName }) => {
    try {
      const r = await api.post(`/bookings/${b.id}/countersign`, { signature, signerName });
      toast.success("Contratto controfirmato: impegno confermato");
      setSignOpen(false);
      contractPdf(r.data);
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); onChanged(); }
  };
  const reject = async () => {
    if (!window.confirm("Vuoi davvero rifiutare questo impegno?")) return;
    try { await api.post(`/bookings/${b.id}/reject`); toast.success("Impegno rifiutato, la data torna libera"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    onChanged();
  };

  return (
    <div className={`card-premium p-5 ${canAnswer || isNew ? "ring-2 ring-gold" : ""}`} data-testid={`booking-${b.id}`}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <span className="flex items-center gap-2"><span className={chip} data-testid={`booking-status-${b.id}`}>{label}</span>{isNew && <span className="chip-gold" data-testid={`booking-new-${b.id}`}>Novità</span>}</span>
        <span className="text-xs text-slate-500">{b.eventDate}</span>
      </div>
      <div className="font-display text-lg font-bold">{party || b.artistName}</div>
      <div className="text-sm text-slate-600">{b.eventLocation} · {b.eventType}</div>
      {!isProvider && b.artistId && !["pending_artist", "rejected", "expired", "cancelled", "broker_request"].includes(b.status) && <div className="mt-1"><SiaeInfo uid={b.artistId} testId={`booking-siae-${b.id}`}/></div>}
      {b.status === "pending_artist" && (
        <div className="mt-2 text-xs text-amber-700 flex items-center gap-1" data-testid={`booking-timer-${b.id}`}><Clock className="w-3.5 h-3.5"/>{hoursLeft(b.expiresAt)}</div>
      )}
      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        {b.totalGross !== undefined && b.status !== "broker_request" ? <span className="font-display text-xl font-bold text-gold">€{b.totalGross.toFixed(2)}</span> : <span/>}
        <div className="flex gap-2 flex-wrap">
        {b.status !== "broker_request" && otherId && (
          <button data-testid={`booking-chat-${b.id}`} onClick={openChat} className="btn-pill-outline text-xs"><MessageSquare className="w-3.5 h-3.5"/>Chat</button>
        )}
        {b.clientSignature && (
          <button data-testid={`booking-pdf-${b.id}`} onClick={() => contractPdf(b)} className="btn-pill-outline text-xs"><Download className="w-3.5 h-3.5"/>Contratto PDF</button>
        )}
        </div>
      </div>
      {canAnswer && (
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button data-testid={`booking-countersign-${b.id}`} onClick={() => setSignOpen(true)} className="btn-pill-gold text-sm justify-center"><PenLine className="w-4 h-4"/>Controfirma</button>
          <button data-testid={`booking-reject-${b.id}`} onClick={reject} className="btn-pill-outline text-sm justify-center"><XCircle className="w-4 h-4"/>Rifiuta</button>
        </div>
      )}
      {canAnswer && (
        <SignContractDialog open={signOpen} onOpenChange={setSignOpen} testPrefix="countersign"
          title="Controfirma il contratto" description={`${b.clientName} ha già firmato. Controfirmando accetti l'impegno.`}
          summary={[["Committente", b.clientName], ["Data", b.eventDate], ["Orario", b.eventTime || "da concordare"], ["Luogo", b.eventLocation], ["Evento", b.eventType], ["Totale", `€${b.totalGross.toFixed(2)}`], ["Note", b.notes || "-"]]}
          confirmLabel="Controfirma e accetta" onConfirm={countersign} />
      )}
    </div>
  );
};
